import { useState } from 'react';

export interface QualityScore {
  overall: number;
  dimensions: {
    adJargon: number;
    readability: number;
    attractiveness: number;
    personaMatch: number;
    creativity: number;
  };
  details: {
    clichesFound: string[];
    emotionalScore: number;
    actionScore: number;
    sentenceComplexity: number;
    keywordMatch: number;
  };
  suggestions: string[];
}

interface QualityBadgeProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export function QualityBadge({ score, size = 'sm', showLabel = true }: QualityBadgeProps) {
  const getColor = (s: number) => {
    if (s >= 80) return '#22c55e'; // green
    if (s >= 60) return '#84cc16'; // lime
    if (s >= 40) return '#eab308'; // yellow
    if (s >= 20) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  const getLabel = (s: number) => {
    if (s >= 80) return '優秀';
    if (s >= 60) return '良好';
    if (s >= 40) return '一般';
    if (s >= 20) return '待改進';
    return '較差';
  };

  const sizes = {
    sm: { badge: '0.75rem', score: '0.875rem', padding: '0.25rem 0.5rem' },
    md: { badge: '0.875rem', score: '1rem', padding: '0.375rem 0.75rem' },
    lg: { badge: '1rem', score: '1.25rem', padding: '0.5rem 1rem' }
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
      <span
        style={{
          background: getColor(score),
          color: 'white',
          fontSize: sizes[size].badge,
          fontWeight: 600,
          padding: sizes[size].padding,
          borderRadius: '9999px',
          minWidth: '2.5rem',
          textAlign: 'center'
        }}
      >
        {score}分
      </span>
      {showLabel && (
        <span style={{ fontSize: sizes[size].score, color: getColor(score), fontWeight: 500 }}>
          {getLabel(score)}
        </span>
      )}
    </div>
  );
}

interface QualityDetailsProps {
  qualityScore: QualityScore;
}

export function QualityDetails({ qualityScore }: QualityDetailsProps) {
  const [isOpen, setIsOpen] = useState(false);

  const dimensions = [
    { key: 'readability', label: '可讀性', icon: '📖' },
    { key: 'attractiveness', label: '吸引力', icon: '✨' },
    { key: 'personaMatch', label: 'Persona匹配', icon: '🎯' },
    { key: 'creativity', label: '創意度', icon: '💡' }
  ];

  const hasIssues = qualityScore.details.clichesFound.length > 0 || 
                    qualityScore.suggestions.length > 0 ||
                    qualityScore.dimensions.adJargon < 0;

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.25rem 0.5rem',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.75rem',
          color: 'var(--color-text-secondary)'
        }}
      >
        <span>{isOpen ? '▼' : '▶'}</span>
        <span>品質詳情</span>
        {hasIssues && <span style={{ color: 'var(--color-warning)' }}>⚠️ 有建議</span>}
      </button>

      {isOpen && (
        <div
          style={{
            marginTop: '0.5rem',
            padding: '0.75rem',
            background: 'var(--color-bg)',
            borderRadius: '0.375rem',
            fontSize: '0.75rem'
          }}
        >
          {/* 維度評分 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {dimensions.map(({ key, label, icon }) => {
              const value = qualityScore.dimensions[key as keyof typeof qualityScore.dimensions];
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{icon}</span>
                  <span style={{ color: 'var(--color-text-secondary)' }}>{label}:</span>
                  <div
                    style={{
                      flex: 1,
                      height: '4px',
                      background: 'var(--color-border)',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(0, value)}%`,
                        height: '100%',
                        background: value >= 15 ? '#22c55e' : value >= 10 ? '#eab308' : '#ef4444',
                        borderRadius: '2px'
                      }}
                    />
                  </div>
                  <span style={{ fontWeight: 500 }}>{value}</span>
                </div>
              );
            })}
          </div>

          {/* 檢測到的問題 */}
          {qualityScore.details.clichesFound.length > 0 && (
            <div style={{ marginBottom: '0.5rem' }}>
              <div style={{ color: 'var(--color-warning)', fontWeight: 600, marginBottom: '0.25rem' }}>
                ⚠️ 廣告腔檢測
              </div>
              <div style={{ color: 'var(--color-text-secondary)' }}>
                發現: {qualityScore.details.clichesFound.join('、')}
              </div>
            </div>
          )}

          {/* 建議 */}
          {qualityScore.suggestions.length > 0 && (
            <div>
              <div style={{ color: 'var(--color-primary)', fontWeight: 600, marginBottom: '0.25rem' }}>
                💡 改進建議
              </div>
              <ul style={{ margin: 0, paddingLeft: '1rem', color: 'var(--color-text-secondary)' }}>
                {qualityScore.suggestions.map((suggestion: string, index: number) => (
                  <li key={index} style={{ marginBottom: '0.125rem' }}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {/* 詳細數據 */}
          <div
            style={{
              marginTop: '0.75rem',
              paddingTop: '0.5rem',
              borderTop: '1px solid var(--color-border)',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.5rem',
              fontSize: '0.7rem',
              color: 'var(--color-text-tertiary)'
            }}
          >
            <div>情感密度: {(qualityScore.details.emotionalScore * 100).toFixed(0)}%</div>
            <div>行動導向: {(qualityScore.details.actionScore * 100).toFixed(0)}%</div>
            <div>複雜度: {(qualityScore.details.sentenceComplexity * 100).toFixed(0)}%</div>
          </div>
        </div>
      )}
    </div>
  );
}
